'use client';

/**
 * JsonRuleBuilder Component (Issue #1480 — Phase D)
 *
 * Glass-styled tree editor for `ApplicabilityRule`. Pure controlled component:
 * the consumer owns the rule state, this component owns only the editing UI.
 *
 * Discriminated tree shape:
 *   - leaf: { field, op, value? }
 *   - all:  { all: ApplicabilityRule[] }
 *   - any:  { any: ApplicabilityRule[] }
 *   - not:  { not: ApplicabilityRule }
 *
 * Used in the loan-product detail panel's "Stage Requirements" tab to make
 * a requirement conditional (e.g. "loanAmount > 1M AND borrowerTier in [2,3]").
 *
 * Theme tokens only — no hardcoded hex/rgba. Typography respects §11b2:
 * `body2` / `caption` / Glass components, no bare HTML headings, no inline
 * fontSize. The component does NOT call tRPC; persistence is the page's job.
 */

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Box, type BoxProps, Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import type { ApplicabilityLeafOp, ApplicabilityLeafRule, ApplicabilityRule } from '@ops-matrix/shared-types';
import { type ReactNode, useId, useRef } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { GlassButton } from '../../GlassButton';
import { GlassCard } from '../../GlassCard';
import { GlassChip } from '../../GlassChip';
import { GlassIconButton } from '../../GlassIconButton';
import { GlassSelect } from '../../GlassSelect';
import { GlassTextField } from '../../GlassTextField';

// ============================================================================
// Types
// ============================================================================

export type RuleNodeKind = 'leaf' | 'all' | 'any' | 'not';

export interface JsonRuleBuilderProps extends Omit<BoxProps, 'onChange' | 'children'> {
  /**
   * Current rule. `null` means "no rule" — the editor will offer an
   * "Add condition" affordance. The consumer owns this state.
   */
  value: ApplicabilityRule | null;
  /**
   * Called whenever the rule changes. Pass the new rule (or `null` to clear).
   */
  onChange: (rule: ApplicabilityRule | null) => void;
  /**
   * Field-name autocomplete for leaf inputs. Free text is still allowed —
   * this is just suggestions. Defaults to the documented context fields.
   */
  availableFields?: ReadonlyArray<string>;
  /**
   * Whether the editor is disabled (read-only display + non-interactive).
   * @default false
   */
  disabled?: boolean;
  /**
   * Internal: when rendered as a child of an `all`/`any` combinator, the
   * parent passes `nested` so we don't re-wrap the child in a GlassCard
   * (would over-nest visually).
   * @default false
   */
  nested?: boolean;
}

const DEFAULT_AVAILABLE_FIELDS: ReadonlyArray<string> = [
  'loanAmount',
  'scopeAmount',
  'borrowerTier',
  'loanPurpose',
  'backgroundReportFlagged',
  'exitCount36mo',
];

const LEAF_OPS: ApplicabilityLeafOp[] = ['eq', 'in', 'gt', 'lt', 'gte', 'lte', 'present'];

// ============================================================================
// Discriminator helpers
// ============================================================================

function getKind(rule: ApplicabilityRule): RuleNodeKind {
  if ('all' in rule) return 'all';
  if ('any' in rule) return 'any';
  if ('not' in rule) return 'not';
  return 'leaf';
}

/**
 * Convert a node from one shape to another. Combinator → leaf throws away
 * children; leaf → combinator wraps the existing leaf as the first child.
 * Reasoning: switching shape is rare and explicit, the data loss is
 * intentional (the user picked a different rule type).
 */
function convertKind(rule: ApplicabilityRule, nextKind: RuleNodeKind): ApplicabilityRule {
  const currentKind = getKind(rule);
  if (currentKind === nextKind) return rule;

  // Pull out the existing leaf or first child (if present) so we don't drop
  // user data on a single-step conversion.
  let firstChild: ApplicabilityRule | null = null;
  if ('all' in rule && rule.all.length > 0) firstChild = rule.all[0]!;
  else if ('any' in rule && rule.any.length > 0) firstChild = rule.any[0]!;
  else if ('not' in rule) firstChild = rule.not;
  else firstChild = rule; // leaf

  switch (nextKind) {
    case 'leaf':
      // Try to use the existing leaf; otherwise start fresh.
      if (firstChild && getKind(firstChild) === 'leaf') return firstChild;
      return { field: '', op: 'eq', value: '' } satisfies ApplicabilityLeafRule;
    case 'all':
      return { all: firstChild ? [firstChild] : [] };
    case 'any':
      return { any: firstChild ? [firstChild] : [] };
    case 'not':
      return { not: firstChild ?? ({ field: '', op: 'eq', value: '' } satisfies ApplicabilityLeafRule) };
  }
}

function emptyLeaf(): ApplicabilityLeafRule {
  return { field: '', op: 'eq', value: '' };
}

/**
 * Apply a partial-leaf update with op-aware value coercion. Pure function —
 * exported so the conversion semantics are unit-testable without driving the
 * MUI listbox in JSDOM (which doesn't reliably support portal-rendered Selects).
 *
 * Coercion rules:
 *  - `op === 'present'` drops the value entirely (the operator takes none).
 *  - Switching to `op === 'in'` from a non-array value resets value to `[]`.
 *  - Switching FROM `'in'` or `'present'` to a scalar op resets value to `''`
 *    so the form doesn't carry an array/undefined where a scalar is expected.
 *
 * Exported for tests; not part of the component's public surface.
 */
export function applyLeafUpdate(
  current: ApplicabilityLeafRule,
  patch: Partial<ApplicabilityLeafRule>,
): ApplicabilityLeafRule {
  const next: ApplicabilityLeafRule = { ...current, ...patch };
  const opChanged = patch.op !== undefined && patch.op !== current.op;

  // Coerce `value` to a default that satisfies the strict per-operator
  // schema in `applicabilityRuleSchema` (#1482 hardening). The defaults
  // below give the user a valid placeholder they can immediately edit:
  //   - `present` → no value (schema rejects extra keys)
  //   - `in` → `['']` (schema requires .min(1))
  //   - `gt`/`lt`/`gte`/`lte` → `0` (schema requires z.number())
  //   - `eq` → `''` (schema requires non-null/undefined)
  if (next.op === 'present') {
    delete next.value;
    return next;
  }
  if (next.op === 'in') {
    if (!Array.isArray(next.value) || next.value.length === 0) {
      next.value = [''];
    }
    return next;
  }
  if (next.op === 'gt' || next.op === 'lt' || next.op === 'gte' || next.op === 'lte') {
    // Coerce non-numeric values (typically '' from a previous scalar op)
    // to 0 so the schema's z.number() check passes.
    if (typeof next.value !== 'number') {
      next.value = 0;
    }
    return next;
  }
  // eq — accepts any defined value; default to '' if op was just changed
  // FROM in/present (the only ops that strip/replace value).
  if (opChanged && (current.op === 'in' || current.op === 'present')) {
    next.value = '';
  }
  if (next.value === undefined || next.value === null) {
    next.value = '';
  }
  return next;
}

// ============================================================================
// Component
// ============================================================================

export function JsonRuleBuilder({
  value,
  onChange,
  availableFields,
  disabled = false,
  nested = false,
  ...boxProps
}: JsonRuleBuilderProps) {
  const { t } = useI18n();

  const fieldSuggestions = availableFields ?? DEFAULT_AVAILABLE_FIELDS;

  // Empty state — render an "Add condition" affordance.
  if (value === null) {
    return (
      <Box {...boxProps}>
        <GlassButton
          variant="ghost"
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => onChange(emptyLeaf())}
          disabled={disabled}
        >
          {t('controlCenter.ruleEditor.addRule')}
        </GlassButton>
      </Box>
    );
  }

  const kind = getKind(value);

  // ────────────────────────────────────────────────────────────────────────
  // Discriminator picker (Leaf / All / Any / Not)
  // ────────────────────────────────────────────────────────────────────────

  const handleKindChange = (nextKind: RuleNodeKind) => {
    onChange(convertKind(value, nextKind));
  };

  const discriminatorRow = (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
        {t('controlCenter.ruleEditor.discriminator')}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {(['leaf', 'all', 'any', 'not'] as const).map((k) => (
          <GlassChip
            key={k}
            size="small"
            label={t(`controlCenter.ruleEditor.${k}`)}
            color={kind === k ? 'primary' : 'default'}
            variant={kind === k ? 'filled' : 'outlined'}
            onClick={disabled ? undefined : () => handleKindChange(k)}
            disabled={disabled}
          />
        ))}
      </Stack>
      {!nested ? <Box sx={{ flex: 1 }} /> : null}
      {!nested ? (
        <GlassIconButton
          size="small"
          onClick={() => onChange(null)}
          disabled={disabled}
          aria-label={t('controlCenter.ruleEditor.removeRule')}
        >
          <DeleteIcon fontSize="small" />
        </GlassIconButton>
      ) : null}
    </Stack>
  );

  // ────────────────────────────────────────────────────────────────────────
  // Body — varies by kind
  // ────────────────────────────────────────────────────────────────────────

  let body: ReactNode;
  if (kind === 'leaf') {
    body = (
      <LeafEditor
        leaf={value as ApplicabilityLeafRule}
        onChange={onChange}
        fieldSuggestions={fieldSuggestions}
        disabled={disabled}
      />
    );
  } else if (kind === 'all' || kind === 'any') {
    const items =
      kind === 'all' ? (value as { all: ApplicabilityRule[] }).all : (value as { any: ApplicabilityRule[] }).any;
    body = (
      <CombinatorEditor
        kind={kind}
        items={items}
        onChange={(nextItems) => onChange(kind === 'all' ? { all: nextItems } : { any: nextItems })}
        availableFields={fieldSuggestions}
        disabled={disabled}
      />
    );
  } else {
    // not
    const inner = (value as { not: ApplicabilityRule }).not;
    body = (
      <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
        <JsonRuleBuilder
          value={inner}
          onChange={(next) => onChange(next === null ? null : { not: next })}
          availableFields={fieldSuggestions}
          disabled={disabled}
          nested
        />
      </Box>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // Wrap in a GlassCard when at root, plain Stack when nested.
  // ────────────────────────────────────────────────────────────────────────

  const inner = (
    <Stack spacing={1.5}>
      {discriminatorRow}
      {body}
    </Stack>
  );

  if (nested) {
    return <Box {...boxProps}>{inner}</Box>;
  }

  return (
    <Box {...boxProps}>
      <GlassCard variant="flat" sx={{ p: 2 }}>
        {inner}
      </GlassCard>
    </Box>
  );
}

JsonRuleBuilder.displayName = 'JsonRuleBuilder';

// ============================================================================
// Leaf Editor — field + op + value (value type adapts to op)
// ============================================================================

interface LeafEditorProps {
  leaf: ApplicabilityLeafRule;
  onChange: (rule: ApplicabilityRule) => void;
  fieldSuggestions: ReadonlyArray<string>;
  disabled: boolean;
}

function LeafEditor({ leaf, onChange, fieldSuggestions, disabled }: LeafEditorProps) {
  const { t } = useI18n();

  const update = (patch: Partial<ApplicabilityLeafRule>) => {
    onChange(applyLeafUpdate(leaf, patch));
  };

  const opOptions = LEAF_OPS.map((op) => {
    const labelKey = `controlCenter.ruleEditor.op${op[0]!.toUpperCase()}${op.slice(1)}` as const;
    return { value: op, label: t(labelKey) };
  });

  // Free-text input doubles as autocomplete via a datalist for accessibility
  // without pulling in a heavier combobox component. `useId()` guarantees a
  // stable, valid, unique id per leaf instance — using `leaf.field` would
  // produce invalid HTML ids (whitespace, duplicates) and break the
  // `<input list>` ↔ `<datalist id>` association.
  const datalistId = useId();

  return (
    <Stack spacing={1}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <GlassTextField
            label={t('controlCenter.ruleEditor.field')}
            value={leaf.field}
            onChange={(e) => update({ field: e.target.value })}
            disabled={disabled}
            fullWidth
            size="small"
            placeholder={t('controlCenter.ruleEditor.fieldPlaceholder')}
            slotProps={{
              htmlInput: {
                list: datalistId,
                'aria-label': t('controlCenter.ruleEditor.field'),
              },
            }}
          />
          <datalist id={datalistId}>
            {fieldSuggestions.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
        </Box>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <GlassSelect
            label={t('controlCenter.ruleEditor.op')}
            value={leaf.op}
            onChange={(e) => update({ op: String(e.target.value) as ApplicabilityLeafOp })}
            options={opOptions}
            disabled={disabled}
            size="small"
            fullWidth
          />
        </Box>
      </Stack>
      {leaf.op === 'present' ? null : leaf.op === 'in' ? (
        <ListValueEditor
          values={Array.isArray(leaf.value) ? (leaf.value as unknown[]) : []}
          onChange={(values) => update({ value: values })}
          disabled={disabled}
        />
      ) : (
        <ScalarValueEditor op={leaf.op} value={leaf.value} onChange={(v) => update({ value: v })} disabled={disabled} />
      )}
    </Stack>
  );
}

// ============================================================================
// Scalar value editor — for eq/gt/lt/gte/lte
// ============================================================================

interface ScalarValueEditorProps {
  op: ApplicabilityLeafOp;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled: boolean;
}

function ScalarValueEditor({ op, value, onChange, disabled }: ScalarValueEditorProps) {
  const { t } = useI18n();

  // Comparison ops want numbers; eq accepts strings/numbers/booleans.
  const numeric = op === 'gt' || op === 'lt' || op === 'gte' || op === 'lte';
  const stringValue = value === null || value === undefined ? '' : String(value);

  return (
    <GlassTextField
      label={t('controlCenter.ruleEditor.value')}
      value={stringValue}
      onChange={(e) => {
        const raw = e.target.value;
        if (numeric) {
          if (raw === '') {
            onChange('');
            return;
          }
          const parsed = Number(raw);
          onChange(Number.isFinite(parsed) ? parsed : raw);
        } else {
          onChange(raw);
        }
      }}
      disabled={disabled}
      fullWidth
      size="small"
      type={numeric ? 'number' : 'text'}
    />
  );
}

// ============================================================================
// List value editor — for `in` (tag-style)
// ============================================================================

interface ListValueEditorProps {
  values: unknown[];
  onChange: (values: unknown[]) => void;
  disabled: boolean;
}

function ListValueEditor({ values, onChange, disabled }: ListValueEditorProps) {
  const { t } = useI18n();

  const handleAdd = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    // Try to coerce numeric strings — `borrowerTier in [2, 3]` needs numbers.
    const asNumber = Number(trimmed);
    const next = Number.isFinite(asNumber) && trimmed === String(asNumber) ? asNumber : trimmed;
    onChange([...values, next]);
  };

  const handleRemove = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={0.75}>
      <GlassTextField
        label={t('controlCenter.ruleEditor.value')}
        size="small"
        fullWidth
        disabled={disabled}
        helperText={t('controlCenter.ruleEditor.valuesHelper')}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const target = e.currentTarget.querySelector('input') as HTMLInputElement | null;
            if (!target) return;
            e.preventDefault();
            handleAdd(target.value);
            target.value = '';
          }
        }}
      />
      {values.length > 0 ? (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {values.map((v, i) => (
            <GlassChip
              key={`${String(v)}-${i}`}
              size="small"
              label={String(v)}
              variant="outlined"
              onDelete={disabled ? undefined : () => handleRemove(i)}
              deleteIcon={<CloseIcon fontSize="small" />}
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

// ============================================================================
// Combinator editor — children list with add/remove
// ============================================================================

interface CombinatorEditorProps {
  kind: 'all' | 'any';
  items: ApplicabilityRule[];
  onChange: (items: ApplicabilityRule[]) => void;
  availableFields: ReadonlyArray<string>;
  disabled: boolean;
}

function CombinatorEditor({ items, onChange, availableFields, disabled }: CombinatorEditorProps) {
  const { t } = useI18n();

  // Stable per-child keys so React doesn't misalign state when a middle child
  // is removed. The keys array is kept in sync with `items` via the local
  // mutation handlers below; if `items` mutates from outside (parent reset,
  // initial value), we reconcile in render by padding/truncating.
  const childKeysRef = useRef<string[]>([]);
  if (childKeysRef.current.length !== items.length) {
    if (childKeysRef.current.length < items.length) {
      while (childKeysRef.current.length < items.length) {
        childKeysRef.current.push(crypto.randomUUID());
      }
    } else {
      childKeysRef.current = childKeysRef.current.slice(0, items.length);
    }
  }

  const addChild = () => {
    childKeysRef.current = [...childKeysRef.current, crypto.randomUUID()];
    onChange([...items, emptyLeaf()]);
  };

  const updateChild = (index: number, next: ApplicabilityRule | null) => {
    if (next === null) {
      childKeysRef.current = childKeysRef.current.filter((_, i) => i !== index);
      onChange(items.filter((_, i) => i !== index));
      return;
    }
    const copy = [...items];
    copy[index] = next;
    onChange(copy);
  };

  return (
    <Stack spacing={1}>
      {items.length === 0 ? (
        <Typography variant="caption" color="textSecondary">
          {t('controlCenter.ruleEditor.noChildren')}
        </Typography>
      ) : null}
      {items.map((child, index) => (
        <Box
          key={childKeysRef.current[index]}
          sx={{
            position: 'relative',
            pl: 2,
            borderLeft: '2px solid',
            borderColor: 'divider',
            py: 0.5,
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <JsonRuleBuilder
                value={child}
                onChange={(next) => updateChild(index, next)}
                availableFields={availableFields}
                disabled={disabled}
                nested
              />
            </Box>
            <GlassIconButton
              size="small"
              onClick={() => updateChild(index, null)}
              disabled={disabled}
              aria-label={t('controlCenter.ruleEditor.removeRule')}
            >
              <DeleteIcon fontSize="small" />
            </GlassIconButton>
          </Stack>
        </Box>
      ))}
      <Box>
        <GlassButton
          variant="ghost"
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={addChild}
          disabled={disabled}
        >
          {t('controlCenter.ruleEditor.addRule')}
        </GlassButton>
      </Box>
    </Stack>
  );
}

export default JsonRuleBuilder;
